import { distance as turfDistance, lineString, point, pointToLineDistance } from '@turf/turf';
import { Convert, Graphooper } from './Graphooper';
import { apiProxyHeaders, graphhopperProxyUrl } from '../apiProxy';
import { PathDetails, pathDetailsQuery } from './segmentPathDetails';

/**
 * Map-matching an existing line string onto the path network.
 *
 * Imported hikes store one hand-traced line per day with no turns attached. Routing it again
 * between two markers would take the shortest path and throw away the author's line, so this
 * asks GraphHopper's Map Matching API to follow the line as drawn instead. The response has
 * the same shape as a routing response, turn instructions included, so it goes through the
 * same `fromGraphooper` conversion as a snapped segment.
 */

/** Our GraphHopper plan rejects a match request with more points than this. */
export const MAX_MATCH_POINTS = 500;

/**
 * Thins a line to at most `maxPoints` of its own vertices, spaced evenly by distance.
 *
 * The first and last vertices always survive, so the match covers the whole segment. Thinning
 * by distance rather than by index keeps a long straight section from eating the budget that
 * a twisty one needs.
 */
export function resampleForMatching(coordinates: number[][], maxPoints: number = MAX_MATCH_POINTS): number[][] {
  const coords = coordinates.map((c) => [c[0], c[1]]);
  if (coords.length <= maxPoints) return coords;

  const cumulative = [0];
  for (let i = 1; i < coords.length; i++) {
    cumulative.push(cumulative[i - 1] + turfDistance(coords[i - 1], coords[i], { units: 'meters' }));
  }
  const total = cumulative[cumulative.length - 1];
  const step = total / (maxPoints - 1);

  const kept = [coords[0]];
  let next = step;
  for (let i = 1; i < coords.length - 1 && kept.length < maxPoints - 1; i++) {
    if (cumulative[i] >= next) {
      kept.push(coords[i]);
      while (next <= cumulative[i]) next += step;
    }
  }
  kept.push(coords[coords.length - 1]);
  return kept;
}

/** GPX body for the match request. GraphHopper does not need timestamps to match a track. */
export function toMatchGpx(coordinates: number[][]): string {
  const points = coordinates.map((c) => `<trkpt lat="${c[1]}" lon="${c[0]}"></trkpt>`).join('');
  return `<?xml version="1.0"?><gpx version="1.1" creator="HiiKER"><trk><trkseg>${points}</trkseg></trk></gpx>`;
}

/**
 * The furthest any of `samples` lies from the matched line, in metres.
 *
 * This is the one number that says whether a match can be trusted: a matcher that jumped onto
 * a parallel lane, or cut a corner the author walked, shows up here as tens of metres.
 */
export function maxDeviationMeters(samples: number[][], matched: number[][]): number {
  // GraphHopper repeats vertices, and a zero-length segment makes turf's nearest-point
  // maths produce NaN, so drop the repeats before measuring.
  const vertices: number[][] = [];
  for (const c of matched) {
    const last = vertices[vertices.length - 1];
    if (!last || last[0] !== c[0] || last[1] !== c[1]) vertices.push([c[0], c[1]]);
  }
  if (samples.length === 0 || vertices.length < 2) return Infinity;
  const line = lineString(vertices);
  let worst = 0;
  for (const c of samples) {
    const d = pointToLineDistance(point([c[0], c[1]]), line, { units: 'meters' });
    if (d > worst) worst = d;
  }
  return worst;
}

/** GraphHopper profile names for the planner's routing styles. */
export function matchProfileFor(profile?: string | null): string {
  switch (profile) {
    case 'bike':
      return 'bike';
    case 'car':
      return 'car';
    default:
      return 'hike';
  }
}

/** Endpoints closer than this to their marker are treated as already on it. */
const ENDPOINT_TOLERANCE_METERS = 1;

/** The parts of a line string feature that joining it to its markers has to keep consistent. */
export interface AnchorableLine {
  lineStringGeometry: { coordinates: number[][] };
  lineStringProperties: {
    instructions?: { d: number }[];
    extras?: { surface?: { values: Array<(string | number)[]> }; distances?: number[][] };
    summary?: { distance: number };
    pathDetails?: PathDetails | null;
  };
}

/**
 * Joins a matched line back onto the markers it runs between.
 *
 * The matcher starts and ends wherever the path network is nearest, which is rarely exactly
 * on the marker. Neighbouring segments and the next day still meet at the marker, so the
 * markers stay put and the line is bridged to them in a straight line.
 *
 * A prepended bridge shifts everything measured from the start: the turn distances move on by
 * its length, and the index ranges of GraphHopper's surface and distance details move on by
 * one vertex. An appended bridge extends the last range to cover the new vertex.
 *
 * The other path details shift the same way but never stretch over a bridge: it is a straight
 * connector on no mapped way, so it has no SAC rating or road class to borrow, as the apps
 * leave it (`padded`).
 */
export function anchorToMarkers<T extends AnchorableLine>(line: T, start: number[], end: number[]): T {
  const coords = line.lineStringGeometry.coordinates;
  if (coords.length === 0) return line;
  const props = line.lineStringProperties;
  const ranges = [props.extras?.surface?.values, props.extras?.distances].filter(Boolean) as Array<
    Array<(string | number)[]>
  >;

  const startGap = turfDistance(start, coords[0], { units: 'meters' });
  if (startGap > ENDPOINT_TOLERANCE_METERS) {
    coords.unshift([...start]);
    for (const instruction of props.instructions || []) instruction.d += startGap;
    for (const values of ranges) {
      for (const range of values) {
        range[0] = (range[0] as number) + 1;
        range[1] = (range[1] as number) + 1;
      }
    }
    for (const values of Object.values(props.pathDetails ?? {})) {
      for (const range of values) {
        range[0] = (range[0] as number) + 1;
        range[1] = (range[1] as number) + 1;
      }
    }
    if (props.summary) props.summary.distance += startGap;
  }

  const endGap = turfDistance(coords[coords.length - 1], end, { units: 'meters' });
  if (endGap > ENDPOINT_TOLERANCE_METERS) {
    coords.push([...end]);
    for (const values of ranges) {
      const last = values[values.length - 1];
      if (last) last[1] = (last[1] as number) + 1;
    }
    if (props.summary) props.summary.distance += endGap;
  }

  return line;
}

export interface MapMatchResult {
  graphooper: Graphooper;
  /** How far the line as drawn strays from the match, measured at the points sent. */
  maxDeviationMeters: number;
}

export async function mapMatchCoordinates(
  coordinates: number[][],
  profile?: string | null,
  fetchFn: typeof fetch = fetch,
): Promise<MapMatchResult> {
  if (coordinates.length < 2) throw new Error('A line needs at least two points to map-match');

  const samples = resampleForMatching(coordinates);
  const response = await fetchFn(
    graphhopperProxyUrl(
      'match',
      `profile=${matchProfileFor(profile)}` +
        '&instructions=true&points_encoded=false' +
        '&details=surface&details=distance' +
        pathDetailsQuery(matchProfileFor(profile)) +
        '&elevation=true',
    ),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/gpx+xml', ...apiProxyHeaders() },
      body: toMatchGpx(samples),
    },
  );
  const text = await response.text();
  if (!response.ok) {
    let message = text;
    try {
      message = JSON.parse(text).message || text;
    } catch (_) {
      // not JSON; keep the raw body
    }
    throw new Error(`Map matching failed: ${message}`);
  }

  const graphooper = Convert.toGraphooper(text);
  const matched = graphooper.paths?.[0]?.points?.coordinates;
  if (!matched || matched.length < 2) throw new Error('Map matching returned no line');

  return { graphooper, maxDeviationMeters: maxDeviationMeters(samples, matched) };
}

/** Staff admins (the admin permission) and super admins see the map-match tool. */
export function canMapMatch(flags: { currentUserIsStaffAdmin?: unknown; currentUserIsAdmin?: unknown }): boolean {
  return flags.currentUserIsStaffAdmin === true || flags.currentUserIsAdmin === true;
}

/** One line telling the admin what the match did, and that undo takes it back. */
export function describeMapMatch(summary: {
  matched: number;
  failed: number;
  maxDeviationMeters: number | null;
  errors: string[];
}): string {
  if (summary.matched === 0 && summary.failed === 0) return 'Nothing to match on this route';
  if (summary.matched === 0) return `Map match failed: ${summary.errors[0] || 'unknown error'}`;

  const segments = `${summary.matched} segment${summary.matched === 1 ? '' : 's'}`;
  const deviation =
    summary.maxDeviationMeters === null ? '' : ` · up to ${Math.round(summary.maxDeviationMeters)} m off the original`;
  const failed = summary.failed > 0 ? ` · ${summary.failed} failed and left as they were` : '';
  return `Matched ${segments}${deviation}${failed}. Undo if it looks wrong.`;
}
